<template>
  <div ref="dropdownRef" class="relative inline-block text-left w-48 sm:w-full sm:max-w-xs">
    <!-- Aktuelle Auswahl -->
    <button
  @click="toggleDropdown"
  @mouseenter="isHovered = true"
  @mouseleave="isHovered = false"
  class="flex items-center sm:w-full sm:max-w-xs w-auto truncate px-2 sm:px-4 py-2 bg-transparent cursor-pointer
         text-skin-b-list-mute hover:text-skin-b-list-base hover:bg-slate-300/40 focus:outline-none"
>
  <!-- Icon Section -->
  <div class="hidden sm:flex items-center flex-shrink">
    <IconEdit
      class="w-6 h-6 mr-2 text-skin-b-list-base transition-opacity duration-300"
    />
  </div>
  <!-- Label Section -->
  <span class="truncate text-left flex-shrink uppercase">{{ $t('common.edit') }}</span>
</button>


    <!-- Dropdown-Menü -->
    <div
      v-if="isOpen"
      class="absolute mt-2 w-full bg-white border-2 border-skin-b-list-bord rounded-md shadow-lg z-10 overflow-hidden"
    >
      <ul class="overflow-y-auto" :style="{ maxHeight: maxDropdownHeight }">
        <li
          v-for="(option, index) in this.filteredOptions"
          :key="option.value"
          @click="() => { option.onClick(); closeDropdown(); }"
          @mouseenter="hoverIndex = index"
          @mouseleave="hoverIndex = -1"
          class="flex items-center px-4 py-2 cursor-pointer uppercase"
          :class="{ 
              'hover:text-skin-b-base hover:bg-skin-b-back' : option.color==undefined,
              'text-red-800 bg-red-100 sm:text-skin-b-base sm:bg-skin-b-list-back sm:hover:text-red-800 sm:hover:bg-red-100' : option.color=='red',
              'text-green-800 bg-green-100 sm:text-skin-b-base sm:bg-skin-b-list-back sm:hover:text-green-800 sm:hover:bg-green-100' : option.color=='green',
          }"
        >
        <IconDelete
            v-if="option.icon == 'delete'"
            class="w-6 h-6 mr-2"
          />
          <IconBlock
            v-if="option.icon == 'block'"
            class="w-6 h-6 mr-2"
          />
          <IconCheck
            v-if="option.icon == 'check'"
            class="w-6 h-6 mr-2"
          />
          <IconStacks
            v-if="option.icon == 'stacks'"
            class="w-6 h-6 mr-2"
          />
          <IconPerson
            v-if="option.icon == 'user'"
            class="w-6 h-6 mr-2"
          />
          <IconColor
            v-if="option.icon == 'color'"
            class="w-6 h-6 mr-2"
          />
          <span>{{ option.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>


<script>
import { ref, toRefs, computed, onMounted, onBeforeUnmount } from 'vue'
import IconEdit from '@/components/icons/IconEdit.vue'
import IconDelete from '@/components/icons/IconDelete.vue'
import IconBlock from '@/components/icons/IconBlock.vue'
import IconCheck from '@/components/icons/IconCheck.vue'
import IconStacks from '@/components/icons/IconStacks.vue'
import IconPerson from '../icons/IconPerson.vue'
import IconColor from '@/components/icons/IconColor.vue'
//import IconSelected from '@/components/icons/IconSelected.vue'
//import IconSort from '@/components/icons/IconSort.vue'
import { useI18n } from 'vue-i18n';

export default {
  name: 'CustomDropdown',
  components: {
    IconEdit, IconDelete, IconBlock, IconCheck, IconStacks, IconColor, IconPerson
  },
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { modelValue, options } = toRefs(props);
    const isOpen = ref(false);
    const hoverIndex = ref(-1);
    const isHovered = ref(false);
    const dropdownRef = ref(null);
    const maxDropdownHeight = ref(`${window.innerHeight - 220}px`);

    const updateMaxDropdownHeight = () => {
      maxDropdownHeight.value = `${window.innerHeight - 220}px`;
    };

    const selectedOption = computed(() =>
      options.value.find(option => option.value === modelValue.value)
    );
    const filteredOptions = computed(() =>
      options.value.filter(option => option.show)
    );

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
    };

    const closeDropdown = () => {
      isOpen.value = false;
    };

    const selectOption = option => {
      emit('update:modelValue', option.value);
      closeDropdown();
    };

    const handleClickOutside = event => {
      if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        closeDropdown();
      }
    };

    const handleKeydown = event => {
      if (event.key === 'ArrowDown') {
        hoverIndex.value = (hoverIndex.value + 1) % filteredOptions.value.length;
      } else if (event.key === 'ArrowUp') {
        hoverIndex.value =
          (hoverIndex.value - 1 + filteredOptions.value.length) %
          filteredOptions.value.length;
      } else if (event.key === 'Enter' && hoverIndex.value >= 0) {
        selectOption(filteredOptions.value[hoverIndex.value]);
      } else if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeydown);
      window.addEventListener('resize', updateMaxDropdownHeight);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('resize', updateMaxDropdownHeight);
    });

    return {
      t,
      isOpen,
      hoverIndex,
      dropdownRef,
      maxDropdownHeight,
      selectedOption,
      filteredOptions,
      isHovered,
      toggleDropdown,
      selectOption,
      closeDropdown,
      updateMaxDropdownHeight,
    };
  },
};
</script>

<style scoped>
button {
  display: flex;
  align-items: center;
  width: 100%;
}

button .flex {
  display: flex;
  align-items: center;
}

button:hover .IconSort {
  opacity: 1;
  transform: translateX(0);
}

.IconSort {
  opacity: 0;
  transform: translateX(-5px);
  transition: opacity 0.3s, transform 0.3s;
}

.IconStacks {
  transition: opacity 0.3s, transform 0.3s;
}

button:hover .IconStacks {
  opacity: 0;
  transform: translateX(5px);
}
</style>


